/**
 * Context carried with a result factsheet so patients can see which timing
 * information was available when a cycle-dependent result was interpreted.
 */

export type ResultCyclePhase =
  | "baseline"
  | "follicular"
  | "stimulation"
  | "ovulatory"
  | "luteal"
  | "unknown";

export type ResultInterpretationContext = {
  /** Menstrual cycle day derived from the result date and cycle start. */
  cycleDay: number | null;
  /** Menstrual phase inferred from cycle day, when a day is available. */
  cyclePhase: ResultCyclePhase;
  /** Treatment phase linked to the result, when available. */
  treatmentPhase: string | null;
  /** The phase window actually used by the biomarker interpretation. */
  usedContext: ResultCyclePhase;
};

export type ResultPhaseLike = {
  id?: string | null;
  type?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

function dateOnly(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = value.split("T")[0];
  return date || null;
}

/** Days between the sample date and cycle start (day 1 = cycle start). */
export function getCycleDayFromResult(
  resultDate: string | null | undefined,
  cycleStartDate: string | null | undefined,
): number | null {
  const startDate = dateOnly(cycleStartDate);
  const drawnDate = dateOnly(resultDate);
  if (!startDate || !drawnDate) return null;

  const start = new Date(`${startDate}T00:00:00`);
  const drawn = new Date(`${drawnDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(drawn.getTime())) {
    return null;
  }

  const dayMs = 1000 * 60 * 60 * 24;
  const cycleDay = Math.floor((drawn.getTime() - start.getTime()) / dayMs) + 1;
  return cycleDay >= 1 ? cycleDay : null;
}

/** Menstrual phase windows used by the client-side hormone interpretation. */
export function getResultCyclePhase(cycleDay: number | null): ResultCyclePhase {
  if (cycleDay === null) return "unknown";
  if (cycleDay >= 2 && cycleDay <= 4) return "baseline";
  if (cycleDay >= 5 && cycleDay <= 13) return "follicular";
  if (cycleDay >= 14 && cycleDay <= 16) return "ovulatory";
  if (cycleDay >= 17 && cycleDay <= 35) return "luteal";
  return "unknown";
}

function phaseContainsDate(phase: ResultPhaseLike, resultDate: string) {
  const date = dateOnly(resultDate);
  const start = dateOnly(phase.startDate);
  const end = dateOnly(phase.endDate);
  if (!date || !start || date < start) return false;
  return !end || date <= end;
}

/**
 * Resolve the timing metadata for a single result. Phase IDs are preferred;
 * dates are used as a fallback for results recorded during a known cycle.
 */
export function resolveResultInterpretationContext({
  resultDate,
  cycleStartDate,
  phaseId,
  phases = [],
}: {
  resultDate?: string | null;
  cycleStartDate?: string | null;
  phaseId?: string | null;
  phases?: ResultPhaseLike[];
}): ResultInterpretationContext {
  const cycleDay = getCycleDayFromResult(resultDate, cycleStartDate);
  const phase =
    (phaseId ? phases.find((candidate) => candidate.id === phaseId) : null) ??
    (resultDate
      ? phases.find((candidate) => phaseContainsDate(candidate, resultDate))
      : null);

  return {
    cycleDay,
    cyclePhase: getResultCyclePhase(cycleDay),
    treatmentPhase: phase?.type ?? null,
    usedContext: "unknown",
  };
}

/**
 * Select the context a hormone-specific classifier would use. This is also
 * used by raw lab-result screens so their factsheets explain an unknown
 * context instead of silently falling back to a day-3 assumption.
 */
export function getTestResultInterpretationContext(
  testName: string,
  base: ResultInterpretationContext,
): ResultInterpretationContext {
  const name = testName.toLowerCase();
  let usedContext: ResultCyclePhase = "unknown";

  const cycleOrTreatmentContext = () => {
    if (base.treatmentPhase === "protocol") return "stimulation" as const;
    if (
      base.treatmentPhase === "transfer" ||
      base.treatmentPhase === "luteal_phase"
    ) {
      return "luteal" as const;
    }
    return base.cyclePhase;
  };

  if (
    name.includes("fsh") ||
    name.includes("follicle stimulating") ||
    name.includes("follicle-stimulating")
  ) {
    usedContext = base.cyclePhase;
  } else if (
    name.includes("estradiol") ||
    name.includes("oestradiol") ||
    /\be2\b/.test(name)
  ) {
    if (base.treatmentPhase === "protocol") {
      usedContext = "stimulation";
    } else if (base.cyclePhase === "baseline") {
      usedContext = "baseline";
    }
  } else if (name.includes("progesterone")) {
    if (base.treatmentPhase === "protocol") {
      usedContext = "stimulation";
    } else if (
      base.treatmentPhase === "transfer" ||
      base.treatmentPhase === "luteal_phase"
    ) {
      usedContext = "luteal";
    } else if (base.treatmentPhase && base.cyclePhase !== "unknown") {
      usedContext = base.cyclePhase;
    }
  } else if (
    /\blh\b/.test(name) ||
    name.includes("luteinizing") ||
    name.includes("luteinising")
  ) {
    usedContext = base.cyclePhase;
  } else if (
    name.includes("endometrial") ||
    name.includes("endometrium") ||
    name.includes("lining") ||
    name.includes("thickness")
  ) {
    usedContext = cycleOrTreatmentContext();
  } else if (
    name.includes("follicle") ||
    name.includes("antral follicle") ||
    /\bafc\b/.test(name)
  ) {
    usedContext = cycleOrTreatmentContext();
  }

  return { ...base, usedContext };
}

export function isCycleContextSensitiveTest(testName: string): boolean {
  return /fsh|follicle[- ]stimulating|progesterone|estradiol|oestradiol|\be2\b|\blh\b|luteinizing|luteinising|endometrial|endometrium|lining|thickness|follicle|antral follicle|\bafc\b/i.test(
    testName,
  );
}
